import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  HostListener,
  ViewChild,
} from '@angular/core';
import { WIZ_EVENT_TYPE, VALUE_ENTER, WizMathUtils, CODE_SPACE } from '@wizeoo/akm-core-lib';
import {
  WizBasePopUp,
  WizDropdown,
  WizFocusManager,
  WizTextInput,
  WizTranslationBasic,
  WizValidationContainerDirective,
  WizEventManager, WizEvent,
  ImageUploadParameter,
  WizImage
} from '@wizeoo/akm-component-lib';
import { Model } from 'src/app/model/model';
import { ALK_EVENT_TYPE, AttachmentService, ColorPickerPop, StyleEngine, TabVO, ViewData } from '@wizeoo/akm-common-lib';
import { AlkEvent } from '@wizeoo/akm-common-lib';
import { Observable, of } from 'rxjs';

@Component({
  selector: 'edit-tab-pop',
  templateUrl: './edit-tab.pop.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class EditTabPop extends WizBasePopUp {

  @ViewChild('tiCode', { read: WizTextInput, static: true }) tiCode: WizTextInput;
  @ViewChild('tiLabel', { read: WizTranslationBasic, static: true }) tiLabel: WizTranslationBasic;
  @ViewChild('tiDescription', { read: WizTranslationBasic, static: true }) tiDescription: WizTranslationBasic;
  @ViewChild('dlTemplate', { read: WizDropdown, static: true }) dlTemplate: WizDropdown;
  @ViewChild('blockBack') blockBack: ElementRef; // zone de dessin de la couleur de background
  /** Composant de sélection de l'icone */
  @ViewChild('uploadImg', { read: WizImage }) uploadImg: WizImage;


  constructor(public elementRef: ElementRef, private cdref: ChangeDetectorRef) {
    super();
  }

  public declare datas: TabVO;
  private clone: TabVO; // clone de travail

  // styles de headers
  public backgroundColor = StyleEngine.STYLE_DEFAULT_BACKGROUND_COLOR; // --grid-header-background-color

  private _isValidable: boolean = false;
  public get isValidable(): boolean {
    return this._isValidable;
  }

  // validation sur enter
  @HostListener('window:keydown', ['$event'])
  keyEvent(event: KeyboardEvent) {
    if (event.key === VALUE_ENTER && this.isValidable) {
      this.validate();
    }
  }

  show() {
    this.clone = new TabVO(Model.getInstance(), {...this.datas.source});
    this.tiCode.wizValue = this.clone.code;

    this.backgroundColor = this.clone.color ? this.clone.color : StyleEngine.STYLE_DEFAULT_BACKGROUND_COLOR;

    this.tiLabel.translationData = Model.getInstance().localeEngine.buildTranslationData(this.clone.labelVO);
    this.tiDescription.translationData = Model.getInstance().localeEngine.buildTranslationData(this.clone.descriptionVO);

    if(!!this.clone.icon) { 
      this.imgSrcObs = AttachmentService.getInstance().downloadAttachmentForWidget(this.clone.icon, false);
      this.imgBlobid = this.clone.icon;
    } else {
      this.imgSrcObs = of(null);
      this.imgBlobid = null;
    }

    // focus du premier champ
    if (this.tiCode) {
      WizFocusManager.getInstance().wizSetFocus(this.tiCode);
    }

    WizEventManager.addListener(this, this, WIZ_EVENT_TYPE.MODEL_UPDATE, this.onModelUpdate);
  }

  onModelUpdate(e: WizEvent = null): void {
    if(e?.component) {
      switch(e.component) {
        case this.uploadImg:
          this.imgSrcObs = AttachmentService.getInstance().downloadAttachmentForWidget(e.value, false);
          this.imgBlobid = e.value;
          this.uploadImg.sourceObservable = this.imgSrcObs;
          break;
      }
    }
    this._isValidable = true;
  }


  validate(): void {

    this.tiLabel.entryDS.getSource().forEach((entry) => this.clone.labelVO.updateTranslationEntry(entry));
    this.tiDescription.entryDS.getSource().forEach((entry) => this.clone.descriptionVO.updateTranslationEntry(entry));
    this.clone.color = this.backgroundColor;
    this.clone.maintenanceFlag = WizMathUtils.createUUID(); // PATCH MAJ SUBCOLL
    this.clone.icon = this.imgBlobid;
    Model.getInstance().tabEngine.merge(this.clone, this.datas, this.onTabMerged.bind(this));
  }

  onTabMerged(tab: TabVO, status: string) {
    if (!!this.callback) {
      this.callback(tab);
    }
    this.hide();
  }

  hide() {
    WizEventManager.removeListener(this, this, WIZ_EVENT_TYPE.MODEL_UPDATE);
    super.hide();
  }

  /* ##############################################################################
                   gestion du colorPicker pour la conf du header
     ############################################################################## */

  /**
   * affichage de la popUp du colorPicker
   * @param pEvent click
   */
   public showColorPicker(pEvent: Event): void {
    let targetColor = null;
    targetColor = this.backgroundColor;
  
    pEvent.stopPropagation();
    pEvent.preventDefault();
    ColorPickerPop.show(this.blockBack.nativeElement, pEvent.target, targetColor, this.backFromPicker.bind(this));
  }

  /**
   * retour de colorPicker, alimentation des couleurs du header
   *
   * @param target le span source pour font ou background
   * @param color la couleur du picker
   */
  public backFromPicker(target: any, color: string): void {
    this.backgroundColor = color;
    this.feedHeaderStyle();
  }

  /** alimentation des slots de couleurs pour le header */
  public feedHeaderStyle(): void {
    this.onModelUpdate();
    this.cdref.markForCheck();
  }

  /** click sur les icones de suppression de style de header */
  public resetColor(who: string): void {
    this.backgroundColor = StyleEngine.STYLE_DEFAULT_BACKGROUND_COLOR; // --grid-header-background-color
    this.feedHeaderStyle();
  }

  public imgSrcObs: Observable<string> = of(null);
  public imgBlobid: string = null;


 /**
   * Demande de chargement d'une image
   */
  public clickForUpload(): void {
    let params: ImageUploadParameter = {
      component: this.uploadImg,
      elementRef: this.uploadImg.elementRef,
      dom: Model.getInstance().domainCode,
      bu: Model.getInstance().businessUnitCode,
      vision: ViewData.wildcardMainVision
    }
    Model.getInstance().batchEngine.loadImage(params);
  }

  /**
   * demande d'ouverture sur l'image picker par clavier; touche entrée ou espace
   * @param event event clavier
   */
  public keydownForUpload(event: KeyboardEvent): void {
    const isEnterKey = event.key === VALUE_ENTER; // touche entrée
    const isSpaceKey = event.code === CODE_SPACE; // touche espace
    if (isEnterKey || isSpaceKey) {
      this.clickForUpload();
    }
  }

  /** affichage de l'icone de suppression des... icones */
  public hasImage(): boolean {
    return !!this.clone?.icon;
  }

  /**  de suppression des... icones */
  public clearImage(): void {
    this.imgSrcObs = of(null);
    this.imgBlobid = null;
    this.uploadImg.sourceObservable = this.imgSrcObs;
    WizEventManager.dispatchEvent(this.uploadImg, new AlkEvent(ALK_EVENT_TYPE.UPDATE_MODEL, true));
  }


}
